import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius } from '@liminal/brand/scale';
import { Input } from '@liminal/web-ui/forms/Input';
import { formatCurrencyAmount } from '@liminal/utils/currency';
import { payOptionNote } from '@liminal/content/rewards';
import { cashbackPercent, groupDecimal, previewOptions, previewRates } from '@/lib/rewards';

interface RatePreviewProps {
  readonly merchantName: string;
  readonly redeemLilPerUsd: string;
  readonly earnLilPerUsd: string;
  /** The Pay with LIL levels' USD as typed, one per row; all empty at a partner without levels. */
  readonly payLevels: readonly string[];
}

function Choice({
  title,
  figure,
  detail,
  muted = false,
}: {
  readonly title: string;
  readonly figure: string;
  readonly detail: string;
  /** A level the payment does not offer: drawn greyed. */
  readonly muted?: boolean;
}) {
  return (
    <Box
      data-testid="rate-preview-choice"
      sx={{
        p: 1.5,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${webColors.border.light}`,
        bgcolor: webColors.surface.secondary,
      }}
    >
      <Typography variant="caption" color={muted ? 'text.disabled' : 'text.secondary'}>
        {title}
      </Typography>
      <Typography variant="subtitle1" color={muted ? 'text.disabled' : undefined}>
        {figure}
      </Typography>
      <Typography variant="caption" color={muted ? 'text.disabled' : 'text.secondary'}>
        {detail}
      </Typography>
    </Box>
  );
}

/**
 * The choice a customer gets on a payment at the rates being set, priced the
 * way Rewards prices it (redeem rounds up, earn rounds down). At a partner
 * with Pay with LIL levels, Pay with LIL is one of the levels, each listed as
 * the app words it, and those the payment does not offer greyed. A preview:
 * the server prices every real payment.
 */
export function RatePreview({ merchantName, redeemLilPerUsd, earnLilPerUsd, payLevels }: RatePreviewProps) {
  const [exampleUsd, setExampleUsd] = useState('20.40');
  const levelled = payLevels.some((usd) => usd.trim() !== '');
  const levels = levelled ? previewOptions(exampleUsd, redeemLilPerUsd, payLevels) : null;
  const preview = levelled && levels === null ? null : previewRates(exampleUsd, redeemLilPerUsd, earnLilPerUsd);
  const cashback = cashbackPercent(earnLilPerUsd, redeemLilPerUsd);
  const where = merchantName.trim() || 'this partner';

  return (
    <Stack spacing={2} data-testid="rate-preview">
      <Input
        label="Example payment (USD)"
        value={exampleUsd}
        onChange={setExampleUsd}
        inputMode="decimal"
        size="sm"
      />
      {preview ? (
        <>
          <Typography variant="body2" color="text.secondary">
            {`After paying ${formatCurrencyAmount(exampleUsd, 'USD')} at ${where}, the customer chooses one:`}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, alignItems: levels ? 'start' : undefined }}>
            {levels ? (
              <Stack spacing={1.5}>
                {levels.map((level) => (
                  <Choice
                    key={level.position}
                    title={`Pay with LIL: ${formatCurrencyAmount(level.usd, 'USD')}`}
                    figure={`${groupDecimal(level.lil)} LIL`}
                    detail={level.offer ? payOptionNote(level.offer) : 'Not offered on this payment'}
                    muted={level.offer === null}
                  />
                ))}
              </Stack>
            ) : (
              <Choice
                title="Pay with LIL"
                figure={`${groupDecimal(preview.redeemLil)} LIL`}
                detail={`gets ${formatCurrencyAmount(exampleUsd, 'USD')} back`}
              />
            )}
            <Choice title="Earn" figure={`${groupDecimal(preview.earnLil)} LIL`} detail="into their rewards" />
          </Box>
          {cashback !== null && (
            <Typography variant="body2">{`Earning is worth ${cashback}% of the payment.`}</Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {levelled
            ? 'Enter both rates, the three levels in rising dollars and an amount to see what the customer is offered.'
            : 'Enter both rates and an amount to see what the customer is offered.'}
        </Typography>
      )}
    </Stack>
  );
}
